import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import ConfirmDeleteDialog from "../../shared/ConfirmDeleteDialog";
import RequiredLabel from "../../shared/RequiredLabel";

/**
 * @param {string[]} values - 入力値の配列
 * @param {function} onChange - 値変更時のコールバック
 * @param {string} [label] - ラベル
 * @param {string} [placeholder] - 入力欄プレースホルダー
 * @param {string} [addButtonLabel] - 追加ボタン文言
 * @param {string} [namePrefix] - name属性のprefix
 * @param {boolean} [unique] - 重複をエラー表示するか
 * @param {boolean} [confirmOnDeleteIfFilled] - 入力済み行削除時に確認を出すか
 * @param {boolean} [isRequired] - 必須ラベルを表示するか
 * @param {function} [renderActions] - 各行右端に追加で表示するアクション。指定しなければ非表示。
 */
function StringList({
  values = [],
  onChange,
  label = "項目",
  placeholder = "入力してください",
  addButtonLabel = "＋追加",
  namePrefix = "items",
  unique = false,
  confirmOnDeleteIfFilled = true,
  isRequired = false,
  renderActions,
}) {
  const [deleteTargetIndex, setDeleteTargetIndex] = useState(null);

  // nemaを取得、なければ空文字
  const getName = (v) => (v && typeof v.name === "string" ? v.name : "");

  // 指定した行だけnemaを更新
  const setNameAt = (arr, idx, newName) =>
    (Array.isArray(arr) ? arr : []).map((v, i) =>
      i === idx ? { ...(v || { id: null, name: "" }), name: newName } : v
    );

  const defaultRow = { id: null, name: "" };

  // 表示は常に実データのみ（空なら0行）
  const displayedValues = values;

  // 重複チェック
  const counts = useMemo(() => {
    const m = new Map();
    values.forEach((v) => {
      const k = getName(v).trim();
      if (!k) return;
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  }, [values]);

  // 空なら1行目を作成、それ以外は対象行だけ更新
  const handleChange = (i, val) => {
    if (!Array.isArray(values) || values.length === 0) {
      onChange([{ id: null, name: val }]);
      return;
    }
    onChange(setNameAt(values, i, val));
  };

  // 「＋追加」ボタンが押されたとき、リストの末尾に空の行を追加
  const handleAdd = () => onChange([...(values || []), defaultRow]);

  // 削除ボタンが押されたとき、その行が空でなければ確認ダイアログを出す
  const askDelete = (i) => {
    const filled = getName(values[i]).trim() !== "";
    if (confirmOnDeleteIfFilled && filled) setDeleteTargetIndex(i);
    else onChange(values.filter((_, idx) => idx !== i));
  };

  // 削除の確認ダイアログで「削除」が押されたとき、リストから削除
  const confirmDelete = () => {
    if (deleteTargetIndex === null) return;
    onChange(values.filter((_, idx) => idx !== deleteTargetIndex));
    setDeleteTargetIndex(null);
  };

  return (
    <Box>
      {(label || isRequired) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {label && <Typography sx={{ fontSize: 14 }}>{label}</Typography>}
          <RequiredLabel show={isRequired} />
        </Box>
      )}

      {displayedValues.map((val, i) => {
        const name = getName(val);
        const trimmed = name.trim();
        const isDup = unique && trimmed && (counts.get(trimmed) || 0) > 1;
        const hasActions = typeof renderActions === "function";

        return (
          <Grid
            container
            spacing={2}
            alignItems="center"
            key={`${namePrefix}-${i}`}
            sx={{ mb: 2 }}
          >
            <Grid item xs={hasActions ? 9 : 11} sm={hasActions ? 10 : 11}>
              <TextField
                fullWidth
                name={`${namePrefix}[${i}]`}
                value={name}
                onChange={(e) => handleChange(i, e.target.value)}
                placeholder={placeholder}
                error={!!isDup}
                sx={{ "& .MuiInputBase-root": { backgroundColor: "#fff" } }}
                helperText={isDup ? "同じ値が既にあります" : undefined}
                FormHelperTextProps={{
                  sx: { textAlign: isDup ? "left" : "right", margin: 0 },
                }}
              />
            </Grid>

            {/* 追加アクションがあるる時だけ表示 */}
            {hasActions && (
              <Grid
                item
                xs={2}
                sm={1}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                {renderActions(val, i)}
              </Grid>
            )}

            {/* 削除 */}
            <Grid item xs={2} sm={1}>
              <IconButton color="error" onClick={() => askDelete(i)}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}

      <Button
        variant="contained"
        onClick={handleAdd}
        sx={{
          backgroundColor: "#818cf8",
          fontWeight: "bold",
          "&:hover": { backgroundColor: "#6366f1" },
        }}
      >
        {addButtonLabel}
      </Button>

      <ConfirmDeleteDialog
        open={deleteTargetIndex !== null}
        onClose={() => setDeleteTargetIndex(null)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
}

export default StringList;
